"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DocumentLifecycleLogicalError = exports.SHORT_SLUG_ADDITION_LENGTH = void 0;
exports.createNewDocumentData = createNewDocumentData;
exports.setRoleData = setRoleData;
exports.setDefaultRoleData = setDefaultRoleData;
exports.makeDocumentPubliclyViewable = makeDocumentPubliclyViewable;
exports.makeDocumentPubliclyCommentable = makeDocumentPubliclyCommentable;
exports.makeDocumentPubliclyEditable = makeDocumentPubliclyEditable;
exports.checkOrganizationAndEnterpriseOwner = checkOrganizationAndEnterpriseOwner;
exports.organizationAndEnterpriseOwnerProperties = organizationAndEnterpriseOwnerProperties;
exports.cloneDocumentData = cloneDocumentData;
const constants_1 = require("@buildwithflux/constants");
const shared_1 = require("@buildwithflux/shared");
const lodash_1 = require("lodash");
const config_1 = require("../config");
const functions_1 = require("../part/functions");
const node_1 = require("../pcb/node");
const permission_1 = require("../permission");
const simulation_1 = require("../simulation");
const constants_2 = require("./constants");
const names_1 = require("./names");
/**
 * Returns a brand new, blank, unpersisted document
 */
function createNewDocumentData(owner, organization = undefined, enterprise = undefined, description = constants_2.documentStrings.defaultProjectDescription, behavior = constants_2.documentStrings.defaultCodeWelcomeMessage) {
    const name = (0, names_1.generateDocumentName)();
    const slug = (0, names_1.slugifyDocumentName)(name);
    const document = {
        uid: (0, shared_1.guid)(),
        name,
        has_custom_name: false,
        description,
        slug,
        created_at: new Date().getTime(),
        updated_at: new Date().getTime(),
        owner_uid: owner.uid,
        owner_handle: owner.handle,
        elements: {},
        nets: {},
        routes: {},
        comment_threads: {},
        routeVertices: {},
        properties: {},
        pcbLayoutRuleSets: {},
        pcbLayoutNodes: {
            [node_1.pcbLayoutRootNodeId]: (0, node_1.createRootNode)(),
        },
        configs: {},
        userUiStates: {},
        active_users: {},
        assets: {},
        roles: {},
        invites: {},
        contributor_uids: [owner.uid],
        belongs_to_part_uid: "",
        copy_of_document_uid: "",
        archived: false,
        behaviour: behavior,
        behaviour_has_errors: false,
        preview_image: "",
        simulation: {
            time_step_size: 5,
            time_step_size_unit: simulation_1.SimulationTimeStepSizeUnits.microsecond,
        },
        fork_count: 0,
        star_count: 0,
        copy_count: 0,
        usage_count: 0,
        unique_count: 0,
        comment_count: 0,
        comment_likes_count: 0,
        // Note, this function is mostly used to create new documents, so we set the default
        // symbolLayoutStrategy to work best for that scenario. However, this plays poorly
        // when loading existing documents in DocumentDataBuilder, so we override it there.
        // See comment there.
        symbolLayoutStrategy: functions_1.DEFAULT_SYMBOL_LAYOUT_STRATEGY,
        features: {
            islandRemoval: "v0",
        },
        ...organizationAndEnterpriseOwnerProperties(organization, enterprise), // Overrides owner_handle, keep this last
    };
    setDefaultRoleData(document);
    return document;
}
exports.SHORT_SLUG_ADDITION_LENGTH = 4;
function createDefaultDocumentRoleData() {
    return {
        [constants_1.ANON_ROLE_UID]: (0, permission_1.createDefaultAnonUserPermissions)(),
    };
}
function createPublicDocumentCanViewRoleData() {
    return {
        [constants_1.ANON_ROLE_UID]: (0, permission_1.createAnonUserPermissions)(permission_1.PermissionType.view),
    };
}
function createPublicDocumentCanCommentRoleData() {
    return {
        [constants_1.ANON_ROLE_UID]: (0, permission_1.createAnonUserPermissions)(permission_1.PermissionType.comment),
    };
}
function createPublicDocumentCanEditRoleData() {
    return {
        [constants_1.ANON_ROLE_UID]: (0, permission_1.createAnonUserPermissions)(permission_1.PermissionType.edit),
    };
}
function setRoleData(documentData, roles) {
    documentData.roles = roles;
    documentData.user_access_list = (0, permission_1.generateUserAccessListForDocument)(documentData, (0, lodash_1.values)(documentData.roles));
    documentData.has_anon_access = documentData.user_access_list.includes(constants_1.ANON_ROLE_UID);
}
/**
 * Set the default role data on a document - i.e. the default anonymous role.
 */
function setDefaultRoleData(documentData) {
    setRoleData(documentData, createDefaultDocumentRoleData());
}
/**
 * Make the document publicly viewable.
 */
function makeDocumentPubliclyViewable(documentData) {
    setRoleData(documentData, createPublicDocumentCanViewRoleData());
}
/**
 * Make the document publicly commentable.
 */
function makeDocumentPubliclyCommentable(documentData) {
    setRoleData(documentData, createPublicDocumentCanCommentRoleData());
}
/**
 * Make the document publicly editable.
 */
function makeDocumentPubliclyEditable(documentData) {
    setRoleData(documentData, createPublicDocumentCanEditRoleData());
}
/**
 * Gets a new set of configs to attach to a cloned document
 *
 * This method returns a new set of configs that could be used instead. To do so, it updates *all* configs
 * (not just those belonging to the original owner) within the given document, replacing the user with the new cloned
 * document's owner, and then deduplicating them by ID
 *
 * Practically this means: after cloning, there are only configs belonging to the new owner, and only one per unique
 * key and section. Exactly which one the owner ends up with is random however; they may end up with another user's
 * config even when cloning their own document!
 *
 * @todo Fix this to e.g. preference configs belonging to the same user, and preference configs belonging to the old document owner
 * @param documentData The given document has already been cloned, and is owned by a new documentData.owner_uid, but has the old configs
 *                     attached
 */
function getNewConfigsForClonedDocument(documentData) {
    return Object.fromEntries(Object.values(documentData.configs).map((config) => {
        const updated = (0, config_1.documentConfigWithIds)(config, documentData.owner_uid);
        return [updated.uid, updated];
    }));
}
function checkOrganizationAndEnterpriseOwner(organizationOwner, enterpriseOwner) {
    if (!organizationOwner) {
        if (enterpriseOwner) {
            throw new DocumentLifecycleLogicalError("Cannot create a document with enterprise owner and no organization owner");
        }
    }
    else if (organizationOwner.enterpriseUid) {
        if (!enterpriseOwner) {
            throw new DocumentLifecycleLogicalError("Cannot create a document with organization belonging to enterprise and no enterprise owner");
        }
        else if (enterpriseOwner.uid !== organizationOwner.enterpriseUid) {
            throw new DocumentLifecycleLogicalError("Cannot create a document with mismatched enterprise and organization owners");
        }
    }
}
function organizationAndEnterpriseOwnerProperties(organizationOwner, enterpriseOwner) {
    checkOrganizationAndEnterpriseOwner(organizationOwner, enterpriseOwner);
    if (!organizationOwner) {
        return {
            organization_owner_uid: undefined,
            organization_member_permission_type: undefined,
            organization_owner_is_enterprise_default: undefined,
            enterprise_owner_uid: undefined,
            enterprise_owner_handle: undefined,
            enterprise_member_permission_type: undefined,
        };
    }
    // Enterprise case
    if (organizationOwner?.enterpriseUid) {
        if (!enterpriseOwner) {
            throw new DocumentLifecycleLogicalError("Cannot create a document with organization belonging to enterprise and no enterprise owner");
        }
        return {
            owner_handle: organizationOwner.handle,
            organization_owner_uid: organizationOwner.uid,
            organization_member_permission_type: permission_1.PermissionTypeDefaultForOrganizationMembers,
            organization_owner_is_enterprise_default: enterpriseOwner.defaultOrganizationUid === organizationOwner.uid,
            enterprise_owner_uid: enterpriseOwner.uid,
            enterprise_owner_handle: enterpriseOwner.handle,
            enterprise_member_permission_type: permission_1.PermissionTypeDefaultForEnterpriseMembers,
        };
    }
    // Non-enterprise case
    return {
        owner_handle: organizationOwner.handle,
        organization_owner_uid: organizationOwner.uid,
        organization_member_permission_type: permission_1.PermissionTypeDefaultForOrganizationMembers,
        organization_owner_is_enterprise_default: undefined,
        enterprise_owner_uid: undefined,
        enterprise_owner_handle: undefined,
        enterprise_member_permission_type: undefined,
    };
}
/**
 * @param user
 * @param existingDocument
 * @param isFork
 * @param organization An optional organization to clone the document to. If not provided, the user will own the document.
 * @param enterprise An optional enterprise to clone the document. If provided, must match the enterprise of the provided organization
 */
function cloneDocumentData(user, existingDocument, isFork, organization, enterprise, isLearningDocument) {
    // Concat the new name with a short uuid to guarantee uniqueness without
    // having to query firestore first if the document name exists
    let copy_of_document_uid = "";
    let fork_of_document_uid = "";
    if (isFork) {
        fork_of_document_uid = existingDocument.uid;
    }
    else {
        copy_of_document_uid = existingDocument.uid;
    }
    const newDocumentData = {
        ...(0, lodash_1.cloneDeep)(existingDocument),
        uid: (0, shared_1.guid)(),
        belongs_to_part_uid: "",
        owner_uid: user.uid,
        owner_handle: user.handle,
        created_at: new Date().getTime(),
        updated_at: new Date().getTime(),
        updated_by: user.handle,
        active_users: {},
        comment_threads: {},
        copy_of_document_uid,
        fork_of_document_uid,
        fork_count: 0,
        star_count: 0,
        copy_count: 0,
        usage_count: 0,
        unique_count: 0,
        comment_count: 0,
        comment_likes_count: 0,
        isLearningDocument: !!isLearningDocument,
        contributor_uids: [user.uid],
        ...organizationAndEnterpriseOwnerProperties(organization, enterprise), // Overrides owner_handle, keep this last
    };
    if (!newDocumentData.routeVertices) {
        newDocumentData.routeVertices = {};
    }
    if (
    // i.e. if changing the owner
    existingDocument.owner_uid !== user.uid &&
        (!existingDocument.organization_owner_uid || existingDocument.organization_owner_uid !== organization?.uid) &&
        (!existingDocument.enterprise_owner_uid ||
            existingDocument.enterprise_owner_uid !== organization?.enterpriseUid) &&
        existingDocument.uid !== constants_2.NEW_PART_TEMPLATE_DOCUMENT_UID) {
        // Note that this doesn't avoid conflicts, we still need to check if this is a unique name with reference to
        // other documents owned by the user or organization - that currently happens outside this function
        newDocumentData.name = existingDocument.name;
    }
    else {
        newDocumentData.name = existingDocument.name + " " + (0, shared_1.guid)().substring(0, exports.SHORT_SLUG_ADDITION_LENGTH);
    }
    /**
     * Each global rule set has a `referenceOfDocumentUid` associated with a document uid.
     *
     * Here since we are cloning a document, we need to update any rule set referencing the existing
     * dorument uid to the new one, to make data consistent, and to avoid bugs like this:
     *
     * https://fluxai.canny.io/admin/feedback/bugreports/p/rules-displaying-additional-groups
     */
    for (const [key, ruleSet] of Object.entries(newDocumentData.pcbLayoutRuleSets)) {
        /**
         * - First condition is for normal clone as described above
         * - Second and third conditions are here to fix `referenceOfDocumentUid` when cloning
         *   a document that already exhibits the bug mentioned above
         */
        if (ruleSet?.referenceOfDocumentUid === existingDocument.uid ||
            ruleSet?.referenceOfDocumentUid === existingDocument.copy_of_document_uid ||
            ruleSet?.referenceOfDocumentUid === existingDocument.fork_of_document_uid) {
            newDocumentData.pcbLayoutRuleSets[key].referenceOfDocumentUid = newDocumentData.uid;
        }
    }
    newDocumentData.slug = (0, names_1.slugifyDocumentName)(newDocumentData.name);
    newDocumentData.roles = {};
    newDocumentData.configs = getNewConfigsForClonedDocument(newDocumentData);
    setDefaultRoleData(newDocumentData);
    return newDocumentData;
}
class DocumentLifecycleLogicalError extends Error {
}
exports.DocumentLifecycleLogicalError = DocumentLifecycleLogicalError;
//# sourceMappingURL=lifecycle.js.map